import { _ as _objectSpread2, a as _slicedToArray } from './slicedToArray-1793c995.esm.js';
import { shadowInvisible, shadows as shadows$1, shadowHalos, shadowHalo, shadowRings, shadowRing, sizes as sizes$1, motion } from '@hover/blueprint-theme';
import { fromEntries, capitalize, isNumber, entries } from '@hover/utilities';
import 'ramda/src/values';
import 'ramda/src/mapObjIndexed';
import 'ramda/src/map';
import 'ramda/src/mergeDeepWith';
import { cssVar, theme } from '@chakra-ui/react';
import _max from 'ramda/src/max';
import _reduce from 'ramda/src/reduce';
import { transitions } from 'polished';

var cssVarPrefix = 'hover';

var configuration = /*#__PURE__*/Object.freeze({
  __proto__: null,
  cssVarPrefix: cssVarPrefix
});

/**
 * Create a CSS variable with the theme prefix. This is mostly useful for
 * referencing the variables that Chakra generates for us from our theme
 * foundation (tokens)
 *
 * @see {@link https://chakra-ui.com/docs/styled-system/css-variables}
 *
 * @param name name of the CSS variable (e.g: `foo` → `--hover-foo`)
 */
var cssVarPrefixed = function cssVarPrefixed(name) {
  return cssVar("".concat(cssVarPrefix, "-").concat(name));
};

/**
 * Helper functions for creating CSS variables to reference the variables
 * that Chakra generates for us from our theme foundation (tokens).
 *
 * @example
 * ```ts
 * variables.size(100).reference // `var(--hover-sizes-100)`
 * variables.size(100).variable // `--hover-sizes-100`
 *
 * @see {@link https://chakra-ui.com/docs/styled-system/css-variables}
 */
var variables = {
  color: function color(_color) {
    return cssVarPrefixed("colors-".concat(_color.split('.').join('-')));
  },
  size: function size(_size) {
    return cssVarPrefixed("sizes-".concat(_size));
  },
  space: function space(_space) {
    return cssVarPrefixed("space-".concat(_space));
  },
  radius: function radius(_radius) {
    return cssVarPrefixed("radii-".concat(_radius));
  }
};

var shadows = /*#__PURE__*/_objectSpread2( /*#__PURE__*/_objectSpread2( /*#__PURE__*/_objectSpread2( /*#__PURE__*/_objectSpread2({}, shadows$1), /*#__PURE__*/fromEntries( /*#__PURE__*/shadowHalos.map(function (halo) {
  return ["halo".concat(capitalize(halo)), shadowHalo(variables.color("halo.".concat(halo)).reference)];
}))), /*#__PURE__*/fromEntries( /*#__PURE__*/shadowRings.map(function (ring) {
  return ["ring".concat(capitalize(ring)), shadowRing(variables.color("ring.".concat(ring)).reference)];
}))), {}, {
  invisibleNeutral: /*#__PURE__*/shadowInvisible( /*#__PURE__*/variables.color('halo.neutral').reference)
});

// NOTE: Chakra's styled-system implementation supports fractions/decimals for
// dimensions, however the default theme defines size presets for `1` and `0.5`
// which overrides this behavior for those two specific values. Here we're
// re-defining `1` and `0.5` to ensure consistent fraction ↔ % behavior
//
// Documentation: https://chakra-ui.com/docs/features/style-props#layout-width-and-height
// Implementation: https://github.com/chakra-ui/chakra-ui/blob/bb0356f10e14886a19072b68f9e4999c6a3bfc1d/packages/styled-system/src/utils/index.ts#L38
//
// Offending space presets: https://github.com/chakra-ui/chakra-ui/blob/bb0356f10e14886a19072b68f9e4999c6a3bfc1d/packages/theme/src/foundations/spacing.ts#L3-L4
// Which get folded into `sizes`: https://github.com/chakra-ui/chakra-ui/blob/bb0356f10e14886a19072b68f9e4999c6a3bfc1d/packages/theme/src/foundations/sizes.ts#L31

var sizes = /*#__PURE__*/_objectSpread2( /*#__PURE__*/_objectSpread2({}, sizes$1), {}, {
  1: '100%',
  0.5: '50%'
});

// Get the highest z-index from Chakra
var highest = /*#__PURE__*/_reduce(_max, 0, /*#__PURE__*/Object.values(theme.zIndices).filter(isNumber));
var zIndices = /*#__PURE__*/_objectSpread2( /*#__PURE__*/_objectSpread2({}, theme.zIndices), {}, {
  loading: highest + 100
});

/**
 * CSS transition preset helpers (applies system easing
 * tokens to polished's `transitions` helper)
 *
 * @example
 * ```tsx
 * import { transition, system } from '@hover/blueprint';
 *
 * <system.div {...transition.easing250('height', 'opacity')} />
 * ```
 */
var transition = /*#__PURE__*/fromEntries( /*#__PURE__*/entries(motion.easings).map(function (_ref) {
  var _ref2 = _slicedToArray(_ref, 2),
    name = _ref2[0],
    easing = _ref2[1];
  var t = function t() {
    for (var _len = arguments.length, args = new Array(_len), _key = 0; _key < _len; _key++) {
      args[_key] = arguments[_key];
    }
    return transitions(args, easing);
  };
  return ["easing".concat(name), t];
}));

export { cssVarPrefixed as a, shadows as b, configuration as c, sizes as s, transition as t, variables as v, zIndices as z };
